import { Injectable, inject, signal } from '@angular/core';
import { IBizEntity, BizEntityTypeCategory, LoggerService, IBizEntityType } from '@optima-stride-ai/core';
import { FileStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { FileCreationService } from '../../../services/file-creation.service';
import { ThumbnailManagerService } from '../../../services/thumbnail-manager.service';
import { LibraryService, IBizEntityWithAccessUrl } from '../library.service';
import { Html2CanvasThumbnailHelper } from '../../../shared/utils/html2canvas-thumbnail.helper';
import { FileExtensions } from '../../../shared/enums/app-constants';
import { FileUploadService } from '../../../services/file-upload.service';
import { WopiServerStateStoreService, ResponseType } from '../../../services/state-store/wopi-server-state-store/wopi-server-state-store.service';
import { AdditionalFieldNames } from '../../../../enum/additional-fields';

/**
 * Service responsible for all file creation and manipulation operations
 * Handles creating documents, spreadsheets, markdown files, PDFs, etc.
 */
@Injectable({
  providedIn: 'root'
})
export class FileOperationsService {
  private readonly fileCreationService = inject(FileCreationService);
  private readonly thumbnailManagerService = inject(ThumbnailManagerService);
  private readonly libraryService = inject(LibraryService);
  private readonly fileStateStoreService = inject(FileStateStoreService);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly logger = inject(LoggerService);
  private readonly wopiServerStateStoreService = inject(WopiServerStateStoreService);

  /** Shares pending initialization across viewer instances for the same entity. */
  private readonly pendingEntityFiles = new Map<string, Promise<IBizEntity>>();

  /**
   * Whether Hayagriva's existing new-file flow supports this category.
   * Uploaded-only formats (images, videos and PDFs) are deliberately unchanged.
   * @param category - Entity category to inspect
   * @returns Whether a blank editable file can be generated
   */
  canCreateBlankFile(category: BizEntityTypeCategory | number): boolean {
    return [
      BizEntityTypeCategory.HayagrivaFileMarkdown,
      BizEntityTypeCategory.HayagrivaFileSpreadsheet,
      BizEntityTypeCategory.HayagrivaFileCSV,
      BizEntityTypeCategory.HayagrivaFileDoc,
      BizEntityTypeCategory.HayagrivaFilePresentation,
      BizEntityTypeCategory.HayagrivaFileDrawing,
      BizEntityTypeCategory.HayagrivaFileWhiteboard,
    ].includes(category);
  }

  // Temporary patch: revisit this initialization flow in the next sprint.
  /**
   * Fetches the authoritative entity, attaches a blank file if its path is missing,
   * and waits for persistence before returning it to the viewer. Concurrent callers
   * share one operation; failed attempts are removed so a later open can retry.
   * @param entityId - Existing BizEntity ID, including externally created entities
  * @param loadedEntity - Optional entity just fetched by the iframe host; reused only when its ID matches
   * @returns The entity with its existing or newly persisted file path
   * @throws When the entity cannot be read, uploaded or updated
   */
  async ensureEntityFile(entityId: string, loadedEntity?: IBizEntity): Promise<IBizEntity> {
    const pending = this.pendingEntityFiles.get(entityId);
    if (pending) return pending;

    const operation = this.attachMissingEntityFile(entityId, loadedEntity);
    this.pendingEntityFiles.set(entityId, operation);
    try {
      return await operation;
    } finally {
      this.pendingEntityFiles.delete(entityId);
    }
  }

  /**
   * Reuses category-specific blank-file generation without creating another entity
   * or imposing new-parent validation on an entity that already exists.
   * @param entityId - Entity whose path is checked
  * @param loadedEntity - Optional server-resolved iframe entity, never decoded capability data
   * @returns The complete entity after file initialization
   */
  private async attachMissingEntityFile(entityId: string, loadedEntity?: IBizEntity): Promise<IBizEntity> {
    const entity = loadedEntity && String(loadedEntity.id) === String(entityId)
      ? loadedEntity
      : await this.libraryService.getBizEntityById(entityId);
    if (!entity) throw new Error(`Entity not found: ${entityId}`);
    if (entity.rights?.read === false) throw new Error('You do not have permission to view this item.');
    if (!this.canCreateBlankFile(entity.type?.category)) return entity;

    const hasPath = entity.additionalInfo?.some(info => {
      if (info.key !== AdditionalFieldNames.PATH && info.fieldMeta?.name !== AdditionalFieldNames.PATH) return false;
      return typeof info.value === 'string' && info.value.trim() !== '' && info.value.trim() !== 'auto';
    });
    if (hasPath) return entity;

    return this.handleNewFileCreation(
      entity.type,
      entity.name || `Untitled-${entity.id}`,
      '',
      { existingEntity: entity }
    );
  }

  // State signals
  fileName = signal<string>('');
  currentThumbnail = signal<string | null>(null);

  /**
   * Create a new document file
   */
  async createDocument(
    item: IBizEntity,
    fileName: string,
    parentId: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    return this.fileCreationService.createDocument(item, fileName, parentId, params);
  }

  /**
   * Create a new spreadsheet file
   */
  async createSpreadsheet(
    item: IBizEntity,
    fileName: string,
    parentId: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    return this.fileCreationService.createSpreadsheet(item, fileName, parentId, params);
  }

  async createCsv(
    item: IBizEntity,
    fileName: string,
    parentId: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    return this.fileCreationService.createCsv(item, fileName, parentId, params);
  }

  /**
   * Create a new markdown file
   */
  async createMarkdown(
    item: IBizEntity,
    fileName: string,
    parentId: string
  ): Promise<IBizEntityWithAccessUrl> {
    return this.fileCreationService.createMarkdown(item, fileName, parentId);
  }

  /**
   * Create a new markdown file with custom content
   */
  async createMarkdownWithContent(
    item: IBizEntity,
    fileName: string,
    content: string,
    parentId: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    return this.fileCreationService.createMarkdownWithContent(item, fileName, content, parentId, params);
  }

  /**
   * Create a new diagram file
   */
  async createDiagram(
    item: IBizEntity,
    fileName: string,
    parentId: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    return this.fileCreationService.createDiagram(item, fileName, parentId, params);
  }

    /**
   * Create a new whiteboard file
   */
  async createWhiteboard(
    item: IBizEntity,
    fileName: string,
    parentId: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    return this.fileCreationService.createWhiteboard(item, fileName, parentId, params);
  }

  /**
   * Create a new presentation file
   */
  async createPresentation(
    item: IBizEntity,
    fileName: string,
    parentId: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    return this.fileCreationService.createPresentation(item, fileName, parentId, params);
  }

  /**
   * Create a new PDF file
   */
  async createPDF(
    item: IBizEntity,
    fileName: string,
    parentId: string
  ): Promise<IBizEntityWithAccessUrl> {
    return this.fileCreationService.createPDF(item, fileName, parentId);
  }

  /**
   * Handle file creation based on category
   */
  async handleNewFileCreation(
    item: IBizEntity | any,
    fileName: string,
    parentId: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    if (item.category === BizEntityTypeCategory.HayagrivaFileMarkdown) {
      const fullFileName = this.ensureFileExtension(fileName, FileExtensions.MD);
      return this.createMarkdownWithContent(
        item,
        fullFileName,
        '# New Document\n\nStart writing your markdown content here...\n',
        parentId,
        params
      );
    } else if (item.category === BizEntityTypeCategory.HayagrivaFileSpreadsheet) {
      const fullFileName = this.ensureFileExtension(fileName, FileExtensions.XLSX);
      return this.createSpreadsheet(item, fullFileName, parentId, params);
    } else if (item.category === BizEntityTypeCategory.HayagrivaFileCSV) {
      const fullFileName = this.ensureFileExtension(fileName, FileExtensions.CSV);
      if (params?.bizEntity) {
        params.bizEntity.name = fullFileName;
      }
      return this.createCsv(item, fullFileName, parentId, params);
    } else if (item.category === BizEntityTypeCategory.HayagrivaFileDoc) {
      const fullFileName = this.ensureFileExtension(fileName, FileExtensions.DOCX);
      return this.createDocument(item, fullFileName, parentId, params);
    } else if (item.category === BizEntityTypeCategory.HayagrivaFilePresentation) {
      const fullFileName = this.ensureFileExtension(fileName, FileExtensions.PPTX);
      return this.createPresentation(item, fullFileName, parentId, params);
    } else if (item.category === BizEntityTypeCategory.HayagrivaFileDrawing) {
      const fullFileName = this.ensureFileExtension(fileName, FileExtensions.ODG);
      return this.createDiagram(item, fullFileName, parentId, params);
    } 
    else if (item.category === BizEntityTypeCategory.HayagrivaFileWhiteboard) {
      const fullFileName = this.ensureFileExtension(fileName, FileExtensions.WHITEBOARD);
      return this.createWhiteboard(item, fullFileName, parentId, params);
    }else {
      throw new Error('Unknown file category for creation');
    }
  }

  /**
   * Ensure file has the correct extension
   */
  private ensureFileExtension(fileName: string, extension: string): string {
    if (!fileName.endsWith(extension)) {
      return fileName + extension;
    }
    return fileName;
  }

  /**
   * Generate thumbnail for a specific file
   */
  async generateThumbnailForFile(file: File): Promise<string | null> {
    try {
      this.currentThumbnail.set(null);
      const thumbnailDataUrl = await this.thumbnailManagerService.generateThumbnailForFile(file);
      if (thumbnailDataUrl) {
        this.currentThumbnail.set(thumbnailDataUrl);
        return thumbnailDataUrl;
      }
      return null;
    } catch (error) {
      this.logger.error(`❌ Thumbnail generation failed: ${error}`);
      return null;
    }
  }

  /**
   * Generate markdown thumbnail for a newly created file and upload it
   */
  async generateMarkdownThumbnailForNewFile(
    file: IBizEntityWithAccessUrl,
    content: string
  ): Promise<void> {
    try {
      this.logger.debug('🖼️ Generating thumbnail for new markdown file...');

      const thumbnailDataUrl = await this.thumbnailManagerService.generateMarkdownThumbnailForNewFile(content);

      if (!thumbnailDataUrl || !thumbnailDataUrl.startsWith('data:image/')) {
        this.logger.warn('⚠️ Failed to generate thumbnail from content, using fallback');
        return;
      }

      // Convert the thumbnail to a File
      const thumbnailFile = this.libraryService.base64ToFile(
        thumbnailDataUrl,
        `${file.name?.replace(FileExtensions.MD, '') || 'document'}-thumb.jpg`
      );

      // Create markdown file for ZIP
      const markdownFile = new File([content], file.name || 'markdown.md', { type: 'text/markdown' });

      // Generate ZIP with both files
      const zipBlob = await Html2CanvasThumbnailHelper.generateZipFile(thumbnailFile, markdownFile);

      // Upload the ZIP file
      await this.uploadMarkdownFilesAsZip(file.id, zipBlob);

      this.logger.info('✅ Thumbnail generated and uploaded for new markdown file');
    } catch (error) {
      this.logger.error(`❌ Error generating thumbnail for new markdown file: ${error}`);
    }
  }

  /**
   * Upload markdown files as ZIP
   */
  private async uploadMarkdownFilesAsZip(bizEntityId: string, zipBlob: Blob): Promise<void> {
    try {
      this.logger.info('📤 Uploading ZIP file with thumbnail and document...');

      // Convert Blob to File for API compatibility
      const zipFile = new File([zipBlob], 'files.zip', { type: zipBlob.type || 'application/zip' });

      this.logger.debug(`ZIP file size: ${zipFile.size} bytes`);

      // Upload using FileStateStoreService
      await this.fileStateStoreService.update(zipFile, bizEntityId);

      this.logger.info('✅ ZIP file uploaded successfully');
    } catch (error) {
      this.logger.error(`❌ Error uploading ZIP file: ${error}`);
      throw error;
    }
  }

  /**
   * Generate thumbnails for multiple uploaded files
   */
  async generateThumbnailsForFiles(uploadedFiles: File[]): Promise<string | null> {
    this.logger.debug(`🖼️ Generating thumbnails for uploaded files: ${uploadedFiles.length} files`);
    if (!uploadedFiles || uploadedFiles.length === 0) {
      return null;
    }

    let lastThumbnailUrl: string | null = null;

    for (const file of uploadedFiles) {
      try {
        const thumbnailUrl = await this.generateThumbnailForFile(file);
        if (thumbnailUrl) {
          lastThumbnailUrl = thumbnailUrl;
        }
      } catch (error) {
        this.logger.error(`❌ Thumbnail generation failed for file: ${file.name}, ${error}`);
      }
    }

    this.logger.debug(`🏁 Finished generating thumbnails. Last thumbnail URL: ${lastThumbnailUrl ? 'Generated' : 'None'}`);
    return lastThumbnailUrl;
  }

  /**
   * Format file size in human-readable format
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Update file name
   */
  setFileName(name: string): void {
    this.fileName.set(name);
  }

  /**
   * Get current file name
   */
  getFileName(): string {
    return this.fileName();
  }

  /**
   * Get current thumbnail
   */
  getCurrentThumbnail(): string | null {
    return this.currentThumbnail();
  }

  /**
   * Create a file from a template by downloading the template file
   * and creating a new file with the same content
   */
  async createFileFromTemplate(
    item: IBizEntity | any,
    fileName: string,
    parentId: string,
    templateFileUrl: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    try {
      // Download the template file
      const response = await this.wopiServerStateStoreService.downloadFile<Blob>(templateFileUrl, {
        responseType: ResponseType.BLOB
      });

      if (!response) {
        throw new Error('Failed to download template file');
      }

      // Determine the correct file extension based on category
      const extension = this.getFileExtensionFromCategory(item.category);
      const fullFileName = this.ensureFileExtension(fileName, extension);

      // Convert blob to File object with the user's chosen filename
      const templateFile = new File([response], fullFileName, { type: response.type });

      // Use performDynamicUpload (which handles thumbnails) instead of uploadFile
      // Pass the file as a single-item array to reuse existing thumbnail generation logic
      return new Promise<IBizEntityWithAccessUrl>(async (resolve, reject) => {
        try {
          // Await the Promise to get the Observable
          const uploadObservable = await this.fileUploadService.performDynamicUpload(parentId, [templateFile], false, params);

          // Now subscribe to the Observable
          uploadObservable.subscribe({
            next: async (bizEntities: IBizEntity[]) => {
              if (!bizEntities || bizEntities.length === 0) {
                this.logger.error('❌ No entities returned from upload');
                reject(new Error('Failed to create file entity'));
                return;
              }

              const bizEntity = bizEntities[0];

              // Check if bizEntity has an ID (could be 'id' or 'biz_entity_id')
              const entityId = bizEntity?.id || (bizEntity as any)?.biz_entity_id || (bizEntity as any)?.bizEntityId;

              if (!bizEntity || !entityId) {
                this.logger.error(`❌ bizEntity or entityId is undefined. Full bizEntity: ${JSON.stringify(bizEntity)}`);
                reject(new Error('Failed to create file entity - no ID returned'));
                return;
              }

              // Fetch the file access URLs to get a complete IBizEntityWithAccessUrl
              try {
                this.logger.info(`📡 Fetching file URLs for bizEntity ID: ${entityId}`);
                const fileWithUrls = await this.fileStateStoreService.getState([entityId]);
                const fileMetadata = Array.isArray(fileWithUrls) ? fileWithUrls[0] : fileWithUrls;

                if (fileMetadata && fileMetadata.name) {
                  this.logger.info(`✅ File with access URLs retrieved: ${fileMetadata.name}`);
                  resolve(fileMetadata as IBizEntityWithAccessUrl);
                } else {
                  // If fileMetadata doesn't have required properties, merge with bizEntity
                  this.logger.warn(`⚠️ File metadata incomplete, merging with bizEntity. FileMetadata name: ${fileMetadata?.name}`);

                  // Create a combined object with both the entity data and file URLs
                  const combinedEntity = {
                    ...bizEntity,
                    id: entityId,
                    fileUrl: fileMetadata?.fileUrl || [],
                    thumbUrl: fileMetadata?.thumbUrl || '',
                    name: bizEntity.name || fileMetadata?.name || fullFileName
                  } as IBizEntityWithAccessUrl;

                  this.logger.info(`✅ Merged entity created: ${combinedEntity.name} (ID: ${combinedEntity.id})`);
                  resolve(combinedEntity);
                }
              } catch (error) {
                this.logger.error(`❌ Error fetching file URLs: ${error}`);
                // Fallback: create entity with ID from bizEntity
                const fallbackEntity = {
                  ...bizEntity,
                  id: entityId,
                  fileUrl: [],
                  thumbUrl: '',
                  name: bizEntity.name || fullFileName
                } as IBizEntityWithAccessUrl;
                resolve(fallbackEntity);
              }
            },
            error: (error) => {
              this.logger.error(`❌ Failed to upload template file: ${error}`);
              reject(error);
            }
          });
        } catch (error) {
          this.logger.error(`❌ Error in upload process: ${error}`);
          reject(error);
        }
      });
    } catch (error) {
      this.logger.error(`❌ Failed to create file from template: ${error}`);
      throw error;
    }
  }

  /**
   * Get file extension based on category
   */
  private getFileExtensionFromCategory(category: number): string {
    switch (category) {
      case BizEntityTypeCategory.HayagrivaFileMarkdown:
        return FileExtensions.MD;
      case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
        return FileExtensions.XLSX;
      case BizEntityTypeCategory.HayagrivaFileCSV:
        return FileExtensions.CSV;
      case BizEntityTypeCategory.HayagrivaFileDoc:
        return FileExtensions.DOCX;
      case BizEntityTypeCategory.HayagrivaFilePresentation:
        return FileExtensions.PPTX;
      case BizEntityTypeCategory.HayagrivaFileDrawing:
        return FileExtensions.ODG;
      case BizEntityTypeCategory.HayagrivaFileWhiteboard:
        return FileExtensions.WHITEBOARD;
      default:
        return '';
    }
  }
}
